import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/dashboard/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { SocialLinkForm } from "@/types/profile";
import { 
  Trash2, Plus, Save, Globe, Upload, ExternalLink, 
  Mail, Image, User, Palette, Copy, Link
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { SitePreview } from "@/components/site-preview/SitePreview";
import { useQueryClient } from "@tanstack/react-query";

const ProfileEditor = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileId, setProfileId] = useState<string | null>(null);
  
  const [savedFullName, setSavedFullName] = useState("");
  const [savedBio, setSavedBio] = useState("");
  const [savedEmail, setSavedEmail] = useState("");
  const [savedAvatarUrl, setSavedAvatarUrl] = useState<string | null>(null);
  const [savedBannerUrl, setSavedBannerUrl] = useState<string | null>(null);
  const [savedShowPoweredBy, setSavedShowPoweredBy] = useState(true);
  const [savedSocialLinks, setSavedSocialLinks] = useState<SocialLinkForm[]>([
    { platform: "website", url: "" }
  ]);
  
  const [fullName, setFullName] = useState("");
  const [bio, setBio] = useState("");
  const [email, setEmail] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [showPoweredBy, setShowPoweredBy] = useState(true);
  const [socialLinks, setSocialLinks] = useState<SocialLinkForm[]>([
    { platform: "website", url: "" }
  ]);
  
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [username, setUsername] = useState<string | null>(null);

  const socialLinksObject = socialLinks.reduce((acc, link) => {
    if (link.platform && link.url) {
      acc[link.platform] = link.url;
    }
    return acc;
  }, {} as Record<string, string>);

  const platformOptions = [
    { id: "website", label: "Website" },
    { id: "instagram", label: "Instagram" },
    { id: "youtube", label: "YouTube" },
    { id: "twitter", label: "Twitter" },
    { id: "facebook", label: "Facebook" },
    { id: "linkedin", label: "LinkedIn" },
    { id: "github", label: "GitHub" },
    { id: "tiktok", label: "TikTok" },
    { id: "spotify", label: "Spotify" },
    { id: "apple", label: "Apple" }
  ];

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        console.log("[ProfileEditor] Fetching user data");
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          console.error("[ProfileEditor] No user found");
          navigate("/login");
          return;
        }

        console.log("[ProfileEditor] Fetching profile for user:", user.id);
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();

        if (error) {
          console.error("[ProfileEditor] Error fetching profile:", error);
          toast.error("Failed to load site data");
          return;
        }

        console.log("[ProfileEditor] Profile data loaded:", profile);
        if (profile) {
          setProfileId(profile.id);
          
          setSavedFullName(profile.full_name || "");
          setSavedBio(profile.bio || "");
          setSavedEmail(profile.email || "");
          setSavedAvatarUrl(profile.avatar_url);
          setSavedBannerUrl(profile.banner_url);
          setSavedShowPoweredBy(profile.show_powered_by ?? true);
          
          setFullName(profile.full_name || "");
          setBio(profile.bio || "");
          setEmail(profile.email || "");
          setAvatarUrl(profile.avatar_url);
          setBannerUrl(profile.banner_url);
          setShowPoweredBy(profile.show_powered_by ?? true);
          
          setUsername(profile.username);

          if (profile.socials) {
            const socials = profile.socials;
            const links: SocialLinkForm[] = Object.entries(socials).map(
              ([platform, url]) => ({ platform, url: url as string })
            );
            const initialLinks = links.length > 0 ? links : [{ platform: "website", url: "" }];
            setSavedSocialLinks(initialLinks);
            setSocialLinks(initialLinks);
          }
        }
      } catch (error) {
        console.error("Error fetching site data:", error);
        toast.error("Failed to load site data");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate]);

  const addSocialLink = () => {
    setSocialLinks([...socialLinks, { platform: "website", url: "" }]);
  };

  const removeSocialLink = (index: number) => {
    const newLinks = [...socialLinks];
    newLinks.splice(index, 1);
    setSocialLinks(newLinks);
  };

  const updateSocialLink = (index: number, field: "platform" | "url", value: string) => {
    const newLinks = [...socialLinks];
    newLinks[index][field] = value;
    setSocialLinks(newLinks);
  };

  const handleAvatarChange = (file: File | null) => {
    if (file) {
      setAvatarFile(file);
      const tempUrl = URL.createObjectURL(file);
      setAvatarUrl(tempUrl);
    }
  };

  const handleBannerChange = (file: File | null) => {
    if (file) {
      setBannerFile(file);
      const tempUrl = URL.createObjectURL(file);
      setBannerUrl(tempUrl);
    }
  };

  const uploadFile = async (file: File, path: string) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
    const filePath = `${path}/${fileName}`;

    const { data, error } = await supabase.storage
      .from('public-profile')
      .upload(filePath, file);

    if (error) {
      throw error;
    }

    const { data: urlData } = supabase.storage
      .from('public-profile')
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  };

  const saveProfile = async () => {
    try {
      setSaving(true);

      if (!profileId) {
        toast.error("Site ID not found");
        return;
      }

      let newAvatarUrl = avatarUrl;
      if (avatarFile) {
        newAvatarUrl = await uploadFile(avatarFile, 'avatars');
      }

      let newBannerUrl = bannerUrl;
      if (bannerFile) {
        newBannerUrl = await uploadFile(bannerFile, 'banners');
      }

      const socialsObject: Record<string, string> = {};
      socialLinks.forEach(link => {
        if (link.platform && link.url) {
          socialsObject[link.platform] = link.url;
        }
      });

      console.log("[ProfileEditor] Updating profile with data:", {
        full_name: fullName,
        bio,
        email,
        avatar_url: newAvatarUrl,
        banner_url: newBannerUrl,
        socials: socialsObject,
        show_powered_by: showPoweredBy
      });

      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: fullName,
          bio,
          email,
          avatar_url: newAvatarUrl,
          banner_url: newBannerUrl,
          socials: socialsObject,
          show_powered_by: showPoweredBy
        })
        .eq("id", profileId);

      if (error) {
        console.error("[ProfileEditor] Error updating profile:", error);
        throw error;
      }

      setSavedFullName(fullName);
      setSavedBio(bio);
      setSavedEmail(email);
      setSavedAvatarUrl(newAvatarUrl);
      setSavedBannerUrl(newBannerUrl);
      setSavedShowPoweredBy(showPoweredBy);
      setSavedSocialLinks([...socialLinks]);
      
      setAvatarFile(null);
      setBannerFile(null);

      queryClient.invalidateQueries({ queryKey: ['userProfile', profileId] });
      queryClient.invalidateQueries({ queryKey: ['userRoleAndUsername', profileId] });
      queryClient.invalidateQueries({ queryKey: ['userProfileComplete', profileId] });

      toast.success("Site updated successfully");
    } catch (error) {
      console.error("Error saving site:", error);
      toast.error("Failed to update site");
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = () => {
    if (username) {
      const fullUrl = `${window.location.origin}/${username}`;
      navigator.clipboard.writeText(fullUrl);
      toast.success("Your site URL has been copied to clipboard");
    }
  };

  const openSiteInNewTab = () => {
    if (username) {
      window.open(`/${username}`, '_blank');
    }
  };

  const renderSiteUrlControls = () => {
    if (!username) return null;
    
    const fullUrl = `${window.location.origin}/${username}`;
    
    return (
      <div className="flex items-center gap-3">
        <div className="bg-muted px-3 py-1.5 rounded-md flex items-center gap-1 text-sm text-muted-foreground">
          <Globe className="size-3.5" />
          <span>{fullUrl}</span>
        </div>
        <Button variant="outline" size="icon" onClick={copyToClipboard} title="Copy URL">
          <Copy size={16} />
        </Button>
        <Button variant="outline" size="icon" onClick={openSiteInNewTab} title="Open Site">
          <ExternalLink size={16} />
        </Button>
      </div>
    );
  };

  return (
    <DashboardLayout
      title="Site Editor"
      description="Customize how your creator site appears to your audience"
      actions={
        <div className="flex items-center gap-4">
          {renderSiteUrlControls()}
          <Button 
            onClick={saveProfile} 
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
            {!saving && <Save className="ml-2 h-4 w-4" />}
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-current border-r-transparent mb-4"></div>
            <p>Loading profile data...</p>
          </div>
        </div>
      ) : (
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Card>
                <CardHeader className="flex flex-row items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <User size={18} />
                      Basic Information
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex flex-col md:flex-row gap-6 items-start">
                    <div className="mx-auto md:mx-0">
                      <div className="relative">
                        <Avatar className="w-32 h-32 border-4 border-background">
                          <AvatarImage src={avatarUrl || undefined} />
                          <AvatarFallback className="text-3xl">{fullName ? fullName[0].toUpperCase() : 'S'}</AvatarFallback>
                        </Avatar>
                        <label 
                          htmlFor="avatar-upload" 
                          className="absolute bottom-2 right-2 bg-primary text-primary-foreground p-2 rounded-full cursor-pointer hover:bg-primary/90 transition-colors shadow-md"
                        >
                          <Upload size={16} />
                        </label>
                        <input 
                          id="avatar-upload" 
                          type="file" 
                          accept="image/*" 
                          className="hidden" 
                          onChange={(e) => handleAvatarChange(e.target.files?.[0] || null)}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground text-center mt-2">Profile Photo</p>
                    </div>

                    <div className="flex-1 space-y-4">
                      <div>
                        <Label htmlFor="fullName" className="text-base font-medium">Name</Label>
                        <Input
                          id="fullName"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          placeholder="Your name"
                          className="mt-1.5"
                        />
                      </div>

                      <div>
                        <Label htmlFor="bio" className="text-base font-medium">Bio</Label>
                        <Textarea
                          id="bio"
                          value={bio}
                          onChange={(e) => setBio(e.target.value)}
                          placeholder="Tell your audience about yourself"
                          className="min-h-[100px] mt-1.5"
                        />
                        <p className="text-xs text-muted-foreground mt-1.5">
                          This will be displayed on your public profile
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-base font-medium">Contact Email</Label>
                        <div className="relative mt-1.5">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="email"
                            type="email"
                            value={email || ""}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="email@example.com"
                            className="pl-10"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1.5">
                          This will be used for the contact button on your site
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette size={18} />
                    Visual Appearance
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label className="text-base font-medium mb-3 block">Banner Image</Label>
                    <div className="relative h-48 w-full rounded-lg overflow-hidden group">
                      {bannerUrl ? (
                        <div 
                          className="w-full h-full bg-cover bg-center"
                          style={{ backgroundImage: `url(${bannerUrl})` }}
                        >
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button 
                              variant="secondary" 
                              onClick={() => document.getElementById('banner-upload')?.click()}
                              className="shadow-lg"
                            >
                              <Upload className="mr-2 h-4 w-4" /> Change Banner
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center border-2 border-dashed border-muted-foreground/25 rounded-lg bg-muted/30">
                          <Image className="h-10 w-10 text-muted-foreground mb-2" />
                          <Button
                            variant="secondary"
                            onClick={() => document.getElementById('banner-upload')?.click()}
                          >
                            <Upload className="mr-2 h-4 w-4" /> Upload Banner Image
                          </Button>
                          <p className="text-xs text-muted-foreground mt-3">
                            Recommended size: 1200 x 300 pixels
                          </p>
                        </div>
                      )}
                      <input
                        id="banner-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleBannerChange(e.target.files?.[0] || null)}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between pt-4">
                    <div>
                      <Label htmlFor="powered-by" className="font-medium">
                        "Powered by Showee" Footer
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Show attribution in the footer of your public site
                      </p>
                    </div>
                    <Switch
                      id="powered-by"
                      checked={showPoweredBy}
                      onCheckedChange={setShowPoweredBy}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Globe size={18} />
                    Social Media Links
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={addSocialLink}
                    disabled={socialLinks.length >= 10}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Link
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {socialLinks.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8">
                        <Globe className="h-10 w-10 text-muted-foreground mb-2" />
                        <p className="text-muted-foreground">No social links added yet</p>
                        <Button 
                          variant="secondary" 
                          size="sm" 
                          onClick={addSocialLink}
                          className="mt-4"
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add your first link
                        </Button>
                      </div>
                    ) : (
                      socialLinks.map((link, index) => (
                        <div key={index} className="flex gap-3 items-center p-3 rounded-md border border-border">
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-3">
                            <div className="md:col-span-2">
                              <select
                                value={link.platform}
                                onChange={(e) => updateSocialLink(index, "platform", e.target.value)}
                                className="flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm"
                              >
                                {platformOptions.map((platform) => (
                                  <option key={platform.id} value={platform.id}>
                                    {platform.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            <div className="md:col-span-3">
                              <Input
                                value={link.url}
                                onChange={(e) => updateSocialLink(index, "url", e.target.value)}
                                placeholder={`Your ${link.platform} URL`}
                                className="w-full"
                              />
                            </div>
                          </div>
                          
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeSocialLink(index)}
                            disabled={socialLinks.length <= 1}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="lg:sticky lg:top-24 lg:self-start">
              <SitePreview 
                username={username} 
                fullName={fullName}
                bio={bio}
                avatarUrl={avatarUrl}
                bannerUrl={bannerUrl}
                socialLinks={Object.keys(socialLinksObject).length > 0 ? socialLinksObject : null}
                email={email}
              />
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ProfileEditor;
